import { Box, ColorOptions, FontSizeOptions, Icon, Spacer, Text } from '@jfs/ui-new'
import { FC, useEffect, useState } from 'react'
import { TableComponent } from '../TableComponent'

const getIconFont = (isChecked?: boolean) => {
  if (isChecked) {
    return 'RADIOBUTTON_ON'
  }
  return 'RADIOBUTTON_OFF'
}

interface RadioButtonProps {
  textLabel?: string
  textSize?: FontSizeOptions | number
  color?: ColorOptions
  onChange?: (isChecked: string) => void | Promise<void>
  value?: string
  checked?: boolean
  loading?: boolean
  testID?: string
  size?: FontSizeOptions | number
  disabled?: boolean
  fontFamily?: string
  textColor?: ColorOptions
}

const RadioButton: FC<RadioButtonProps> = ({
  textLabel = '',
  textSize = 'FONT_SIZE_LARGE',
  color = 'PRIMARY',
  onChange: onPress,
  checked,
  value,
  testID,
  size = 'FONT_SIZE_EXTRA_LARGE',
  disabled = false,
  fontFamily,
  textColor = 'BLACK',
}) => {
  let isCheck = checked
  if (value !== undefined) {
    isCheck = value === 'true'
  }

  const onSelected = async () => {
    if (onPress && !disabled) {
      onPress(isCheck ? 'false' : 'true')
    }
  }
  return (
    <Box
      flexDirection="row"
      hoverColor="BG_SECONDARY_BUTTON_HOVER"
      padding={8}
      paddingRight={24}
      marginRight={24}
      alignItems="center"
      onPress={onSelected}
      underlayColor="LIGHTEST_GREY_2"
      minHeight={48}>
      <Box underlayColor="LIGHTEST_GREY_2" onPress={onSelected} testID={testID}>
        <Icon id={getIconFont(isCheck)} color={isCheck ? color : 'DARK_GREY'} size={size} />
      </Box>
      {textLabel !== '' && (
        <>
          <Spacer direction="horizontal" size={8} />
          <Box>
            <Text
              type="h2"
              title={textLabel}
              fontSize={textSize}
              color={isCheck ? color : textColor}
              fontFamily={fontFamily}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

interface RadioButtonGroupProps {
  color?: ColorOptions
  onChange?: (isChecked: string) => void | Promise<void>
  value?: string
  loading?: boolean
  data: []
  direction?: 'vertical' | 'horizontal'
  testID?: string
  error?: string
  touched?: boolean
  disabled?: boolean
  hide?: boolean
  size?: FontSizeOptions | number
  textSize?: FontSizeOptions | number
  fontFamily?: string
  textColor?: ColorOptions
  noOfColumns?: number
}

const RadioButtonGroup: FC<RadioButtonGroupProps> = (props) => {
  const {
    value,
    data,
    direction = 'vertical',
    color,
    onChange,
    testID,
    error,
    touched,
    disabled = false,
    hide,
    size,
    textSize,
    fontFamily,
    textColor,
    noOfColumns,
  } = props
  const [selected, setSelected] = useState(value)
  const isError = touched && !!error

  useEffect(() => {
    setSelected(value)
    if (onChange && !!value) {
      onChange(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  if (hide) {
    return null
  }

  const selectItem = (label: string) => {
    setSelected(label)
    if (onChange) {
      onChange(label)
    }
  }

  const renderRadioItem = ({ item, index }) => {
    return (
      <RadioButton
        testID={`${testID}${index}`}
        key={index.toString()}
        color={color}
        textLabel={data[item]}
        checked={selected === item}
        onChange={() => {
          selectItem(item)
        }}
        disabled={disabled}
        size={size}
        textSize={textSize}
        fontFamily={fontFamily}
        textColor={textColor}
      />
    )
  }

  const listContent: any = Object.keys(data)
  const contentsList = () => {
    return (
      <TableComponent
        value={''} //Routes value
        canFetchMore={false}
        testID={'ListID'}
        ListContent={listContent ?? []}
        hide={false}
        isCustom={true}
        RenderCustomElement={renderRadioItem}
        numColumns={noOfColumns}
        horizontal={direction === 'horizontal'}
        extraData={selected}
        keyExtractor={(item) => String(item)}
      />
    )
  }

  return (
    <Box>
      <Box gap={direction === 'vertical' ? undefined : 'MEDIUM'}>{contentsList()}</Box>
      {isError ? (
        <>
          <Spacer direction="vertical" size={8} />
          <Text type="h4" color="ERROR">
            {error}
          </Text>
        </>
      ) : null}
    </Box>
  )
}

export { RadioButton, RadioButtonGroup }
